import { buildCallsChip, buildValueChip, buildStats, buildMemberStats, buildUnassignedNote } from './chipRows.js';

/**
 * Team Insights redesign — what a page needs to hand a section to <InsightSection> (a mixin for TeamInsights.vue and
 * TeamInsightsCustomReports.vue, so both build the numbers the same way). It counts nothing itself: everything comes from the arrays and
 * objects the classic charts already use, through chipRows.js.
 *
 * The page provides: formatName(), dateFormatStr, allSettings, employees (all from mixin.js / the page).
 */

// The second list on a section's team card: what the classic page shows as "Total scheduled / Total received", word for word.
export const TI_VALUE_CHIPS = {
  scheduled: { id: 'scheduled', label: 'Total scheduled', short: 'Scheduled', unit: 'appointment', valueLabel: 'Scheduled value', noun: 'appointment(s)', title: 'Appointment(s) Scheduled', valueKey: 'AppointmentValue', valueColumn: 'Appointment Value' },
  claims: { id: 'received', label: 'Total received', short: 'Received', unit: 'claim', valueLabel: 'Claim value', noun: 'claim(s)', title: 'Claim(s) Received', valueKey: 'ClaimValue', valueColumn: 'Claim Value' },
  preauth: { id: 'received', label: 'Total Received', short: 'Received', unit: 'appointment', valueLabel: 'Received value', noun: 'appointment(s)', title: 'Appointment(s) Scheduled', valueKey: 'AppointmentValue', valueColumn: 'Appointment Value' },
  // Team Insights (Today / Week / Month) Aging tabs: "Claims received from follow-ups", with the column each classic popover has
  claimsPaid: { id: 'received', label: 'Claims received from follow-ups', short: 'Received', unit: 'claim', valueLabel: 'Claim value', noun: 'claim(s)', title: 'Claim(s) Received', valueKey: 'ClaimValue', valueColumn: 'Insurance Paid Amt' },
  claimsEstimate: { id: 'received', label: 'Claims received from follow-ups', short: 'Received', unit: 'claim', valueLabel: 'Claim value', noun: 'claim(s)', title: 'Claim(s) Received', valueKey: 'ClaimValue', valueColumn: 'Insurance Estimate' },
};

// The six sections of Today / Week-to-Date / Month-to-Date, in the order the classic page lists them. `prefix` names the page's computeds:
// <prefix>Data<Period> (the team), <prefix>TasksData<Period> (the follow-ups), <prefix><second><Period> (the scheduled / received items),
// <prefix>Data<Period>ByEmp (the people). `goal` is the settings name (<goal>_<DAILY|WEEKLY|MONTHLY>_CALLS_GOAL); only Overdue Recall and
// Unscheduled Treatment have one. `group`: main | aging | broken (Aging has its own tabs, like the classic page).
export const TI_SECTIONS = [
  { id: 'overdue', group: 'main', title: 'Overdue Recall', prefix: 'overdueRecall', second: 'ScheduledData', which: 'scheduled', goal: 'OVERDUE_RECALL', subject: 'overdue_recall' },
  { id: 'unscheduled', group: 'main', title: 'Unscheduled Treatment', prefix: 'unscheduledTreatment', second: 'ScheduledData', which: 'scheduled', goal: 'UNSCHEDULED_TREATMENT', subject: 'unscheduled_treatment' },
  { id: 'claims', group: 'aging', tab: 'Outstanding Claims', title: 'Outstanding Claims', prefix: 'outstandingClaims', second: 'ClaimReceivedData', which: 'claimsPaid', subject: 'outstanding_claim' },
  { id: 'preauth', group: 'aging', tab: 'Pre-authorizations', title: 'Outstanding Pre-Authorizations', prefix: 'preAuthOutstanding', second: 'ClaimReceivedData', which: 'claimsEstimate', subject: 'outstanding_preauths' },
  { id: 'balances', group: 'aging', tab: 'Patient Balances', title: 'Patient Balances', prefix: 'patientBalances', second: null, which: null, subject: 'patient_balances' },
  { id: 'broken', group: 'broken', title: 'Broken Appointment', prefix: 'brokenAppointment', second: 'ScheduledData', which: 'scheduled', subject: 'broken_appointment' },
];

// `memberView`: 'outcomes' = one bar per person split by outcome (Today, where the classic page draws a pie per person), 'trend' = a bar chart per
// person (Week: one bar group per day, Month: per week). `timeRange` words the "busiest …" sentence.
export const TI_PERIODS = {
  Daily: { word: 'Daily', subtitle: 'Follow-ups logged today', memberView: 'outcomes', timeRange: 'day' },
  Weekly: { word: 'Weekly', subtitle: 'Follow-ups, week to date', memberView: 'trend', timeRange: 'day' },
  Monthly: { word: 'Monthly', subtitle: 'Follow-ups, month to date', memberView: 'trend', timeRange: 'week' },
};

export default {
  computed: {
    // How the redesigned lists format a name, a date and money — the same filters / helpers the classic popovers use.
    tiFmt() {
      const filters = this.$options.filters;
      const symbol = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL ? this.allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue : undefined;
      return {
        name: p => this.formatName(p),
        date: d => filters.dateformat(d, this.dateFormatStr),
        money: v => filters.money(v, symbol),
        moneyTotal: v => filters.money(v),
      };
    },
  },
  methods: {
    // The redesigned team card's lists, built from the very arrays the classic popovers loop over (see redesign/chipRows.js).
    tiCallsChip(items) {
      return buildCallsChip(items, this.tiFmt);
    },

    tiValueChip(items, which) {
      return buildValueChip(items, TI_VALUE_CHIPS[which], this.tiFmt);
    },

    // The team card's four numbers. `which` names the section's second measure (null for Patient Balances: follow-ups only).
    tiTeamStats(team, items, which) {
      return buildStats(team ? team.totalCalls : 0, items, which ? TI_VALUE_CHIPS[which] : null, this.tiFmt);
    },

    // Says why the person cards add up to less than the team when they do (the item is credited to someone with no card). '' otherwise.
    tiStatsNote(members, items, which) {
      const nameOf = (id) => {
        const e = (this.employees || []).find(x => x.EmployeeID === id);
        return e ? e.Name : '';
      };
      return buildUnassignedNote(items, members, which ? TI_VALUE_CHIPS[which] : null, nameOf);
    },

    // Each person's card gets the same four numbers: their classic total, the scheduled / received items they created, and the
    // Conversion Rate the classic code already stored on them (conversionRate).
    tiMembers(members, items, which) {
      return buildMemberStats(members || [], items, which ? TI_VALUE_CHIPS[which] : null, this.tiFmt);
    },

    // The classic goal line, same arithmetic: "N% of Daily Goal Met" = ((total / parseInt(goal, 10)) * 100).toFixed(0). null when the site has no
    // usable goal (the classic page would show NaN% there). `fill` is the bar's width, capped at 100 like the classic progress bar.
    tiGoal(total, base, word) {
      const setting = base && this.allSettings ? this.allSettings[`${base}_${word.toUpperCase()}_CALLS_GOAL`] : null;
      const target = setting ? parseInt(setting.PropertyValue, 10) : NaN;
      if (!(target > 0)) return null;
      return { word, target, percent: ((total / target) * 100).toFixed(0), fill: Math.min(100, (total / target) * 100) };
    },

    // Every section of one tab, ready for <InsightSection v-bind>. period: 'Daily' | 'Weekly' | 'Monthly'. `ok` is the classic "has anything
    // to show" test (follow-ups made, or something scheduled / received); false ⇒ the page shows its EmptyState, as the classic page does.
    tiPeriodSections(period) {
      const P = TI_PERIODS[period];
      return TI_SECTIONS.map((def) => {
        const team = this[`${def.prefix}Data${period}`];
        const tasks = this[`${def.prefix}TasksData${period}`];
        const second = def.second ? (this[`${def.prefix}${def.second}${period}`] || []) : [];
        const byEmp = this[`${def.prefix}Data${period}ByEmp`] || [];
        const ok = !!(((team && team.totalCalls > 0) || second.length > 0) && (!def.goal || this.allSettings));
        const base = { id: def.id, group: def.group, tab: def.tab, title: def.title, subject: def.subject, ok };
        if (!ok) return base;
        const trend = P.memberView === 'trend';
        // Week and Month hand each person's bar-chart data in the {ticks, data} form; the classic page runs it through transformBarChartData.
        const people = trend ? byEmp.map(m => Object.assign({}, m, { chartData: this.transformBarChartData(m.chartData) })) : byEmp;
        return Object.assign(base, {
          subtitle: P.subtitle,
          team,
          memberView: P.memberView,
          timeRange: P.timeRange,
          members: trend ? this.tiMembers(people, second, def.which) : people,
          stats: this.tiTeamStats(team, second, def.which),
          // The note talks about person cards that carry scheduled / received numbers, so only where they do (Week / Month).
          statsNote: trend ? this.tiStatsNote(byEmp, second, def.which) : '',
          chips: [this.tiCallsChip(tasks)].concat(def.which ? [this.tiValueChip(second, def.which)] : []),
          goal: def.goal ? this.tiGoal(team ? team.totalCalls : 0, def.goal, P.word) : null,
        });
      });
    },
  },
};
